import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import IconButton from '@mui/material/IconButton'
import Chip from '@mui/material/Chip'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { listPolicies, listRoles, bulkUpdatePolicies, getRd, listResourceTypes, type Policy, type Role, type ResearchDomain, type ResourceType } from '../services/authApi'

const ACTIONS = ['create', 'read', 'update', 'delete', 'publish', 'share', 'use']
// user, the bare agent, and the three OBO trust tiers. A tier with no rows for a
// role falls back to the bare 'agent' policy; a configured tier is authoritative.
const ACTOR_TYPES = ['user', 'agent', 'agent:trusted', 'agent:standard', 'agent:restricted'] as const
const ACTOR_LABELS: Record<string, string> = {
  user: 'User',
  agent: 'Agent',
  'agent:trusted': 'Agent · Trusted',
  'agent:standard': 'Agent · Standard',
  'agent:restricted': 'Agent · Restricted',
}

export function PolicyMatrixPage() {
  const { rdId } = useParams<{ rdId: string }>()
  const navigate = useNavigate()
  const [rd, setRd] = useState<ResearchDomain | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [policies, setPolicies] = useState<Policy[]>([])
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([])
  const [selectedRole, setSelectedRole] = useState('')
  const [actorTab, setActorTab] = useState(0)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [dirty, setDirty] = useState(false)

  // Local editable state
  const [matrix, setMatrix] = useState<Record<string, boolean>>({})              // "rt:action" → allowed
  const [viewAllMap, setViewAllMap] = useState<Record<string, boolean>>({})       // "rt" → viewAll
  const [modifyAllMap, setModifyAllMap] = useState<Record<string, boolean>>({})   // "rt" → modifyAll

  const load = useCallback(async () => {
    if (!rdId) return
    try {
      const [rdData, rolesData, policiesData, rtData] = await Promise.all([
        getRd(rdId),
        listRoles(rdId),
        listPolicies(rdId),
        listResourceTypes(),
      ])
      setRd(rdData.rd)
      setRoles(rolesData)
      setPolicies(policiesData)
      setResourceTypes(rtData)
      if (rolesData.length > 0 && !selectedRole) {
        setSelectedRole(rolesData[0]!.name)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    }
  }, [rdId, selectedRole])

  useEffect(() => { load() }, [load])

  // Rebuild the matrix when role/actorType/policies/resourceTypes change
  const actorType = ACTOR_TYPES[actorTab]!
  useEffect(() => {
    const m: Record<string, boolean> = {}
    const va: Record<string, boolean> = {}
    const ma: Record<string, boolean> = {}
    for (const rt of resourceTypes) {
      // viewAll/modifyAll: read from any policy for this role/actor/resourceType (they're the same across actions)
      const anyPolicy = policies.find(
        p => p.role === selectedRole && p.actorType === actorType && p.resourceType === rt.key
      )
      va[rt.key] = !!anyPolicy?.viewAll
      ma[rt.key] = !!anyPolicy?.modifyAll

      for (const action of ACTIONS) {
        const key = `${rt.key}:${action}`
        const policy = policies.find(
          p => p.role === selectedRole && p.actorType === actorType && p.resourceType === rt.key && p.action === action
        )
        m[key] = policy?.effect === 'allow'
      }
    }
    setMatrix(m)
    setViewAllMap(va)
    setModifyAllMap(ma)
    setDirty(false)
    setSaved(false)
  }, [selectedRole, actorTab, policies, resourceTypes])

  function handleToggle(resourceType: string, action: string) {
    const key = `${resourceType}:${action}`
    setMatrix(prev => ({ ...prev, [key]: !prev[key] }))
    setDirty(true)
    setSaved(false)
  }

  function handleViewAllToggle(resourceType: string) {
    setViewAllMap(prev => ({ ...prev, [resourceType]: !prev[resourceType] }))
    setDirty(true)
    setSaved(false)
  }

  function handleModifyAllToggle(resourceType: string) {
    setModifyAllMap(prev => ({ ...prev, [resourceType]: !prev[resourceType] }))
    setDirty(true)
    setSaved(false)
  }

  async function handleSave() {
    if (!rdId) return
    try {
      const policyEntries = Object.entries(matrix)
        .filter(([, allowed]) => allowed)
        .map(([key]) => {
          const [resourceType, action] = key.split(':')
          return {
            actorType,
            resourceType: resourceType!,
            action: action!,
            effect: 'allow' as const,
            viewAll: viewAllMap[resourceType!] ?? false,
            modifyAll: modifyAllMap[resourceType!] ?? false,
          }
        })

      await bulkUpdatePolicies(rdId, { role: selectedRole, policies: policyEntries })
      await load()
      setSaved(true)
      setDirty(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save policies')
    }
  }

  if (!rdId) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate(`/rds/${rdId}`)} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3">Policy Matrix</Typography>
          <Typography variant="body2" color="text.secondary">{rd?.name}</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Policies saved for {selectedRole}/{actorType}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Role</Typography>
          <Select
            size="small"
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}
            sx={{ minWidth: 160, display: 'block' }}
          >
            {roles.map(r => <MenuItem key={r.name} value={r.name}>{r.name}</MenuItem>)}
          </Select>
        </Box>

        <Box>
          <Typography variant="caption" color="text.secondary">Actor Type</Typography>
          <Tabs
            value={actorTab}
            onChange={(_, v) => setActorTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}
          >
            {ACTOR_TYPES.map((at, i) => (
              <Tab key={at} label={
                <Chip
                  label={ACTOR_LABELS[at]}
                  size="small"
                  variant={actorTab === i ? 'filled' : 'outlined'}
                  color={at === 'user' ? 'primary' : 'secondary'}
                />
              } />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={!dirty}
        >
          Save Changes
        </Button>
      </Box>

      <Paper sx={{ border: '1px solid', borderColor: 'divider', overflow: 'auto' }} elevation={0}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th, & td': { px: 1.5, py: 1, textAlign: 'center' } }}>
          <thead>
            <tr>
              <Box component="th" sx={{ textAlign: 'left !important', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                <Typography variant="overline">Resource</Typography>
              </Box>
              {ACTIONS.map(action => (
                <th key={action}>
                  <Typography variant="overline">{action}</Typography>
                </th>
              ))}
              <th><Tooltip title="View All — skip record-level checks for read"><Typography variant="overline" color="info.main">View All</Typography></Tooltip></th>
              <th><Tooltip title="Modify All — skip record-level checks for read/update/delete"><Typography variant="overline" color="warning.main">Modify All</Typography></Tooltip></th>
            </tr>
          </thead>
          <tbody>
            {resourceTypes.map(rt => (
              <Box
                component="tr"
                key={rt.key}
                sx={{ '&:hover': { bgcolor: 'action.hover' }, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box component="td" sx={{
                  textAlign: 'left !important', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1,
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{rt.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{rt.key}</Typography>
                </Box>
                {ACTIONS.map(action => {
                  const key = `${rt.key}:${action}`
                  const checked = matrix[key] ?? false
                  return (
                    <td key={action}>
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => handleToggle(rt.key, action)}
                        sx={{
                          color: checked ? 'primary.main' : 'text.disabled',
                          '&.Mui-checked': { color: 'primary.main' },
                        }}
                      />
                    </td>
                  )
                })}
                <td>
                  <Switch size="small" checked={viewAllMap[rt.key] ?? false} onChange={() => handleViewAllToggle(rt.key)} color="info" />
                </td>
                <td>
                  <Switch size="small" checked={modifyAllMap[rt.key] ?? false} onChange={() => handleModifyAllToggle(rt.key)} color="warning" />
                </td>
              </Box>
            ))}
          </tbody>
        </Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Checked = allow, unchecked = deny (secure default). <strong>View All</strong> = skip record-level checks for reads.
        <strong> Modify All</strong> = skip record-level checks for all actions. Changes apply to <strong>{selectedRole}/{actorType}</strong> only.
      </Typography>
      {actorType.startsWith('agent:') && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <strong>Agent trust tier.</strong> If you leave <em>every</em> cell unchecked for this tier, it inherits the bare
          <strong> Agent</strong> policy (backward-compatible). As soon as you check any cell, this tier becomes authoritative —
          unchecked cells then mean <strong>deny</strong>, so a more-restricted tier can never exceed a broader one.
        </Alert>
      )}
    </Box>
  )
}
